import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * DensityToggle Component and Context
 *
 * Enterprise polish: Users can control UI density.
 * Comfortable = spacious, easier scanning
 * Compact = dense, power-user mode
 *
 * Usage:
 * 1. Wrap app with DensityProvider:
 *    <DensityProvider>
 *      <App />
 *    </DensityProvider>
 *
 * 2. Use the toggle:
 *    <DensityToggle />
 *
 * 3. Use density in components:
 *    const { density, isCompact } = useDensity();
 *    <div className={isCompact ? 'p-2' : 'p-4'}>...</div>
 */

const DensityContext = createContext({
  density: 'comfortable',
  isCompact: false,
  setDensity: () => {},
  toggleDensity: () => {},
});

export const useDensity = () => useContext(DensityContext);

export const DensityProvider = ({ children, defaultDensity = 'comfortable' }) => {
  const [density, setDensity] = useState(() => {
    // Load from localStorage
    const saved = localStorage.getItem('arise-ui-density');
    return saved || defaultDensity;
  });

  useEffect(() => {
    localStorage.setItem('arise-ui-density', density);
    // Add class to body for global CSS hooks
    document.body.classList.remove('density-comfortable', 'density-compact');
    document.body.classList.add(`density-${density}`);
  }, [density]);

  const toggleDensity = () => {
    setDensity(prev => prev === 'comfortable' ? 'compact' : 'comfortable');
  };

  return (
    <DensityContext.Provider value={{
      density,
      isCompact: density === 'compact',
      setDensity,
      toggleDensity,
    }}>
      {children}
    </DensityContext.Provider>
  );
};

export const DensityToggle = ({ showLabel = true, className = '' }) => {
  const { density, toggleDensity } = useDensity();
  const isCompact = density === 'compact';

  return (
    <button
      onClick={toggleDensity}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors ${
        isCompact
          ? 'bg-subtle border-line text-ink-muted'
          : 'bg-elevated border-line text-ink-muted hover:bg-muted'
      } ${className}`}
      title={isCompact ? 'Switch to comfortable view' : 'Switch to compact view'}
    >
      {/* Toggle Icon */}
      <div className="relative w-5 h-5">
        {isCompact ? (
          // Compact icon - tight lines
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
        ) : (
          // Comfortable icon - spaced lines
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h16M4 12h16M4 19h16" />
          </svg>
        )}
      </div>

      {showLabel && (
        <span className="text-xs font-medium">
          {isCompact ? 'Compact' : 'Comfortable'}
        </span>
      )}
    </button>
  );
};

/**
 * Helper hook for density-aware spacing
 */
export const useDensitySpacing = () => {
  const { isCompact } = useDensity();

  return {
    // Padding
    p: isCompact ? 'p-2' : 'p-4',
    px: isCompact ? 'px-2' : 'px-4',
    py: isCompact ? 'py-1.5' : 'py-3',

    // Gaps
    gap: isCompact ? 'gap-2' : 'gap-4',
    gapSm: isCompact ? 'gap-1' : 'gap-2',
    gapLg: isCompact ? 'gap-3' : 'gap-6',

    // Margins
    mb: isCompact ? 'mb-2' : 'mb-4',
    mt: isCompact ? 'mt-2' : 'mt-4',
    my: isCompact ? 'my-2' : 'my-4',

    // Text
    text: isCompact ? 'text-sm' : 'text-base',
    textSm: isCompact ? 'text-xs' : 'text-sm',

    // Rounds
    rounded: isCompact ? 'rounded-lg' : 'rounded-xl',

    // Card height
    cardHeight: isCompact ? 'min-h-0' : 'min-h-[80px]',
  };
};

/**
 * DensityAware wrapper component
 * Applies appropriate spacing based on density setting
 */
export const DensityAware = ({
  children,
  comfortable = {},
  compact = {},
  className = '',
}) => {
  const { isCompact } = useDensity();
  const appliedStyles = isCompact ? compact : comfortable;

  return (
    <div className={`${appliedStyles.className || ''} ${className}`} style={appliedStyles.style}>
      {children}
    </div>
  );
};

export default DensityToggle;
